<script lang="ts">
  import Layout from "$app/Layouts/Simple.svelte"
  import { xhr, toast } from "$app/utils"
  import { fade } from "svelte/transition"
  import Honeybadger from "$app/utils/honeybadger"
  import { Phone, Mail } from "lucide-svelte"

  let props = $props()
  let { recaptcha_site_key_v3 }: { recaptcha_site_key_v3: string } = props

  import contact_bg_img from "@images/contact/contact-hero-bg.png"

  const initialVals = {
    name: "",
    email: "",
    company_name: "",
    phone: "",
    subject: "General Inquiry",
    message: ""
  }

  let form = $state({ ...initialVals })
  let errors = $state({})

  let sending = $state(false)
  let sent = $state(false)
  let recaptcha_token: string = $state("")
  let submitTimes = 0

  async function postContact() {
    errors = {}
    sending = true
    try {
      const resp = await xhr.post(`/contact`, {
        "g-recaptcha-response-data": recaptcha_token,
        contact: form
      })

      form = { ...initialVals }
      sent = true
      sending = false
    } catch (e) {
      toast("There was an error submiting the contact form.", "error")
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      if (e.response) {
        errors = { ...errors, ...e.response.data.errors }
      }
    }
  }

  function submitForm(): void {
    window.grecaptcha
      .execute(recaptcha_site_key_v3, { action: "signup" })
      .then((token) => {
        recaptcha_token = token
        submitTimes++
        postContact()
      })
  }
</script>

<svelte:head>
  <script
    src="https://www.google.com/recaptcha/api.js?render={recaptcha_site_key_v3}"
    async
    defer
  ></script>
</svelte:head>

<Layout {...props}>
  <div class="relative">
    <div
      class="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl grid grid-cols-1 lg:grid-cols-2 lg:gap-8"
    >
      <div class="relative px-4 flex items-start">
        <div class="absolute lg:inset-0 lg:top-0">
          <img
            src={contact_bg_img}
            class="z-0 w-full h-full object-left-top object-scale-down"
            alt="Contact Background"
          />
        </div>
        <div
          class="z-10 flex flex-col mx-auto mt-12 md:mt-48 md:px-12 justify-center"
        >
          <h1
            class="mx-auto z-10 text-left text-black text-3xl font-bold tracking-loose leading-loose sm:text-4xl lg:text-5xl"
          >
            <span class="block">We'd Love To</span>
            <span class="block mt-4">Hear From You</span>
          </h1>

          <dl class="mt-8 text-base text-gray-800">
            <div>
              <dt class="sr-only">Postal address</dt>
              <dd>
                <p>
                  Lead With Purpose, Inc.<br />
                  4140 Oceanside Boulevard Suite 159 #347<br />
                  Oceanside, CA 92056
                </p>
              </dd>
            </div>
            <div class="mt-6">
              <dt class="sr-only">Phone number</dt>
              <dd class="flex">
                <Phone class="h-6 w-6 text-gray-400" />
                <span class="ml-3">+1 (760) 283-8834</span>
              </dd>
            </div>
            <div class="mt-3">
              <dt class="sr-only">Email</dt>
              <dd class="flex">
                <Mail class="h-6 w-6 text-gray-400" />
                <span class="ml-3">support@leadwithpurpose.com</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <div class="relative py-12 lg:py-20">
        <div class="mx-auto max-w-md">
          <h2
            class="text-left text-black text-xl font-bold tracking-loose leading-loose sm:text-2xl lg:text-3xl"
          >
            How can we help?
          </h2>

          <p class="text-lg text-gray-800 mt-2">
            Drop a message below and we'll get back to you as soon as possible.
          </p>
          {#if sent}
            <div
              in:fade
              out:fade
              class="border border-green-800 p-8 rounded-lg bg-green-100 mt-6 mb-56"
            >
              <p class="text-lg text-gray-900">
                Thank you for sending your message. We will get back to you
                shortly.
              </p>
            </div>
          {:else}
            <form
              onsubmit={(e) => {
                e.preventDefault()
                submitForm()
              }}
              in:fade
              out:fade
            >
              {#if Object.keys(errors).length > 0}
                <div class="bg-red-500 text-white p-3">
                  There are some errors in the form that prohibited to create
                  the account, please correct the errors.
                </div>
              {/if}

              {#if errors.recaptcha}
                <div class="text-red-600 text-center font-semibold my-4">
                  {errors.recaptcha}
                </div>
              {/if}
              <input
                type="hidden"
                name="g-recaptcha-response-data"
                bind:value={recaptcha_token}
              />
              <div>
                <label
                  for="first_name"
                  class="block text-sm font-medium text-gray-900">Name</label
                >
                <div class="mt-1 input-cont" class:error={errors.name}>
                  <input
                    type="text"
                    bind:value={form.name}
                    required
                    class="py-3 px-4 block w-full shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                  <div class="msg">{errors.name}</div>
                </div>
              </div>
              <div>
                <label
                  for="email"
                  class="block text-sm font-medium text-gray-900">Email</label
                >
                <div class="mt-1 input-cont" class:error={errors.email}>
                  <input
                    type="email"
                    required
                    bind:value={form.email}
                    autocomplete="email"
                    class="py-3 px-4 block w-full shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                  <div class="msg">{errors.email}</div>
                </div>
              </div>
              <div>
                <div class="flex justify-between">
                  <label
                    for="phone"
                    class="block text-sm font-medium text-gray-900"
                    >Company Name</label
                  >
                  <span id="phone-optional" class="mt-2 text-xs text-gray-400"
                    >Optional</span
                  >
                </div>
                <div class="mt-1">
                  <input
                    type="text"
                    bind:value={form.company_name}
                    class="py-3 px-4 block w-full shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <div class="flex justify-between">
                  <label
                    for="phone"
                    class="block text-sm font-medium text-gray-900"
                    >Phone Number</label
                  >
                  <span id="phone-optional" class="mt-2 text-xs text-gray-400"
                    >Optional</span
                  >
                </div>
                <div class="mt-1">
                  <input
                    type="tel"
                    bind:value={form.phone}
                    autocomplete="tel"
                    class="py-3 px-4 block w-full shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label
                  for="subject"
                  class="block text-sm font-medium text-gray-900">Subject</label
                >
                <div class="mt-1">
                  <select
                    bind:value={form.subject}
                    class="mt-1 block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option selected>General Inquiry</option>
                    <option>Technical Support</option>
                    <option>Enterprise Volume Discount</option>
                    <option>Coaching Inquiry</option>
                    <option>Speaking Request</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div>
                <div class="flex justify-between">
                  <label
                    for="message"
                    class="block text-sm font-medium text-gray-900"
                    >Message</label
                  >
                </div>
                <div class="mt-1">
                  <textarea
                    name="message"
                    bind:value={form.message}
                    required
                    rows="4"
                    class="py-3 px-4 block w-full shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  ></textarea>
                </div>
              </div>
              <div class="mt-4">
                <button
                  type="submit"
                  disabled={sending}
                  class="btn-rounded success btn-big w-64 h-14"
                >
                  Send Message
                </button>
              </div>
            </form>
          {/if}
        </div>
      </div>
    </div>
  </div>
</Layout>
